import { isNil, isUndefined } from 'lodash-es'
import { v4 as uuidv4 } from 'uuid'

import { createEvents, EventEmitter } from './event'
import { BridgeMessage } from './message'

export type BridgeEventListener<T = unknown> = (payload: T) => void

type Environment = {
  isNativeApp: boolean
  platform: 'ios' | 'android' | 'web'
}

type PendingPromise<T = unknown> = {
  resolve: (value: T | PromiseLike<T>) => void
  reject: (error: Error) => void
  timeout: NodeJS.Timeout | null
}

const environment = process.env.NEXT_PUBLIC_ENVIRONMENT

export class Bridge {
  environment: Environment
  private emitter: EventEmitter
  private pendingPromises = new Map<string, PendingPromise>()
  private debug = environment !== 'live'

  constructor() {
    this.emitter = createEvents()
    this.environment = this.setupEnvironment()
    this.setupNativeMessageListener()
  }

  private setupNativeMessageListener() {
    if (isUndefined(typeof window) || isNil(window.bridge)) {
      return
    }

    window.bridge.eventTarget.addEventListener('bridge', ((event: Event) => {
      const customEvent = event as CustomEvent<BridgeMessage>
      this.handleNativeMessage(customEvent.detail)
    }) as EventListener)
  }

  private setupEnvironment() {
    if (isUndefined(typeof window)) {
      return { isNativeApp: false, platform: 'web' as const }
    }

    const hasWebKit = !!window.webkit?.messageHandlers?.iOSWebview
    const hasAndroid = !!window.androidWebview

    if (hasWebKit) return { isNativeApp: true, platform: 'ios' as const }
    if (hasAndroid) return { isNativeApp: true, platform: 'android' as const }

    return { isNativeApp: false, platform: 'web' as const }
  }

  private handleNativeMessage({ type, payload, metadata }: BridgeMessage) {
    if (metadata?.messageId && this.pendingPromises.has(metadata.messageId)) {
      const pending = this.pendingPromises.get(metadata.messageId)!

      if (pending.timeout) {
        clearTimeout(pending.timeout)
      }

      this.pendingPromises.delete(metadata.messageId)
      pending.resolve(payload)

      return
    }

    this.emitter.emit(type, payload)
  }

  private generateMessageId() {
    return `${uuidv4()}_${Date.now()}`
  }

  closeWebviewPopup() {
    if (this.debug) {
      console.log('[Bridge] closeWebviewPopup')
    }

    if (!this.environment.isNativeApp) {
      return
    }

    if (this.environment.platform === 'ios') {
      this.postMessage('closeWebviewPopup')
    }

    if (this.environment.platform === 'android') {
      window.androidWebview.closeWebviewPopup()
    }
  }

  postMessage(message: BridgeMessage | string): void {
    if (this.debug) {
      console.log('[Bridge] Message from Web:', message)
    }

    if (!this.environment.isNativeApp) {
      return
    }

    if (this.environment.platform === 'ios') {
      window.webkit.messageHandlers.iOSWebview.postMessage(message)
    }

    if (this.environment.platform === 'android') {
      window.androidWebview.postMessage(JSON.stringify(message))
    }
  }

  postMessageAsync<T = unknown>(
    messageProp: BridgeMessage,
    delay: number | null = 10000,
  ): Promise<T> {
    const messageId = this.generateMessageId()

    return new Promise((resolve, reject) => {
      const message: BridgeMessage = { ...messageProp, metadata: { messageId } }

      let timeout: NodeJS.Timeout | null = null

      if (delay) {
        timeout = setTimeout(() => {
          this.pendingPromises.delete(messageId)
          reject(new Error(`Bridge message timeout: ${message.type}`))
        }, delay)
      }

      this.pendingPromises.set(messageId, {
        resolve: resolve as PendingPromise['resolve'],
        reject,
        timeout,
      })

      // 메시지 전송
      this.postMessage(message)
    })
  }

  addEventListener<T = unknown>(
    type: string,
    listener: BridgeEventListener<T>,
  ) {
    const unsubscribe = this.emitter.on(type, listener)

    return unsubscribe
  }
}

export const createBridge = () => {
  return new Bridge()
}
